import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc } from "firebase/firestore";

export default function AdminTestimonials() {
    const [formData, setFormData] = useState({
        authorName: "",
        authorTitle: "",
        eventName: "",
        rating: 5,
        text: ""
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [testimonials, setTestimonials] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const fetchTestimonials = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "testimonials"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            setTestimonials(fetched);
        } catch (error) {
            console.error("Failed to fetch testimonials:", error);
        }
    };

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            const numericId = editingId ? formData.id : Date.now();

            const testimonialData = {
                id: numericId,
                authorName: formData.authorName,
                authorTitle: formData.authorTitle,
                eventName: formData.eventName,
                rating: parseInt(formData.rating),
                text: formData.text,
                updatedAt: serverTimestamp()
            };

            if (!editingId) {
                testimonialData.createdAt = serverTimestamp();
                await addDoc(collection(db, "testimonials"), testimonialData);
                setMessage("Testimonial successfully added!");
            } else {
                await setDoc(doc(db, "testimonials", editingId), testimonialData, { merge: true });
                setMessage("Testimonial successfully updated!");
            }
            
            cancelEdit();
            fetchTestimonials(); 
        } catch (error) {
            console.error("Error saving testimonial:", error);
            setMessage("Failed to save testimonial: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (testi) => {
        setEditingId(testi.dbId);
        setFormData({
            id: testi.id,
            authorName: testi.authorName || "",
            authorTitle: testi.authorTitle || "",
            eventName: testi.eventName || "",
            rating: testi.rating || 5,
            text: testi.text || ""
        });
        setMessage("Editing testimonial from: " + testi.authorName);
    };

    const handleDelete = async (testi) => {
        if (!window.confirm("Are you sure you want to delete this testimonial from '" + testi.authorName + "'?")) return;
        try {
            await deleteDoc(doc(db, "testimonials", testi.dbId));
            setMessage("Testimonial deleted.");
            fetchTestimonials();
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ authorName: "", authorTitle: "", eventName: "", rating: 5, text: "" });
        setMessage("");
    };

    const styles = {
        container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif" },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        title: { fontSize: '2.5rem', fontWeight: '400', color: '#003049', margin: 0 },
        mainFlex: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
        formSection: { flex: 1, minWidth: '400px', background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' },
        listSection: { flex: 1, minWidth: '400px', padding: '10px', height: '600px', overflowY: 'auto' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none' },
        textarea: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none', minHeight: '100px', resize: 'vertical' },
        submitBtn: { padding: '15px', background: '#F07167', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold', fontSize: '16px', width: '100%', marginTop: '10px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: '1.5rem', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        testiCard: { padding: '20px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative' },
        testiAuthor: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049', paddingRight: '60px' },
        testiDetails: { margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.6' },
        cardActions: { position: 'absolute', top: '20px', right: '20px', display: 'flex', flexDirection: 'row', gap: '10px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Testimonial" : "Add New Testimonial"}</h1>
            </div>

            <div style={styles.mainFlex}>
                <div style={styles.formSection}>
                    {message && (
                        <div style={message.includes("Failed") ? styles.errorMsg : styles.msg}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Author Name</label>
                            <input name="authorName" value={formData.authorName} onChange={handleInput} style={styles.input} required placeholder="e.g. Amara Okonkwo" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Author Title (Role, Company)</label>
                            <input name="authorTitle" value={formData.authorTitle} onChange={handleInput} style={styles.input} required placeholder="e.g. Lead Product Designer, Stripe" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Event Name</label>
                            <input name="eventName" value={formData.eventName} onChange={handleInput} style={styles.input} required placeholder="e.g. React Summit 2025" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Rating (1-5)</label>
                            <input name="rating" type="number" min="1" max="5" value={formData.rating} onChange={handleInput} style={styles.input} required />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Testimonial Text</label>
                            <textarea name="text" value={formData.text} onChange={handleInput} style={styles.textarea} required placeholder="What did they say?" />
                        </div>

                        <div style={{display: 'flex', gap: '10px'}}>
                            <button type="submit" style={styles.submitBtn} disabled={loading}>
                                {loading ? "Saving..." : (editingId ? "Update Testimonial" : "Add Testimonial")}
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} style={{...styles.submitBtn, background: '#eee', color: '#333', boxShadow: 'none'}}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="no-scrollbar" style={styles.listSection}>
                    <h2 style={styles.listTitle}>Published Testimonials ({testimonials.length})</h2>
                    {testimonials.length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#777' }}>No testimonials found.</p>
                    ) : (
                        testimonials.map(testi => (
                            <div key={testi.dbId} style={styles.testiCard}>
                                <div style={{flex: 1}}>
                                    <h3 style={styles.testiAuthor}>{testi.authorName}</h3>
                                    <p style={styles.testiDetails}>
                                        <strong>Title:</strong> {testi.authorTitle} <br />
                                        <strong>Event:</strong> {testi.eventName} <br />
                                        <strong>Rating:</strong> {testi.rating}/5 <br />
                                        <br />
                                        <em>"{testi.text}"</em>
                                    </p>
                                </div>
                                <div style={styles.cardActions}>
                                    <button onClick={() => handleEdit(testi)} style={styles.actionBtn} title="Edit Testimonial">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </button>
                                    <button onClick={() => handleDelete(testi)} style={styles.deleteBtn} title="Delete Testimonial">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
